import { Box, Button, Grid, LinearProgress, Typography } from "@mui/material";
import React, { useEffect, useState, useContext } from "react";
import NofityUserContext from "context/notifyUserContext/NotifyUserContext";
import { API_ENDPOINTS } from "config/ApiConfig";
import LessonContext from "context/lessonContext/LessonContext";
import usePostRequest from "hooks/usePostRequest";
import useGetRequest from "hooks/useGetRequest";
import QuizFeedback from "./QuizFeedback";
import QuizTimerStartOverlay from "overlay/quizComponentOverlay";
import QuizTimer from "./TimerCard";
import QuizResultCard from "./QuizResultCard";
import VideoPlayerSkeleton from "components/common/skeleton/videoPlayerSkeleton";

const MultipleOptionsQuiz = ({ content }) => {
  // Existing state declarations remain the same
  const [display, setLoading] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [activeOption, setActiveOption] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [contentDetails, setContentDetails] = useState(null);
  const [contentDetails1, setContentDetails1] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);
  const [timerKey, setTimerKey] = useState(0);
  const [estimatedCompletionTime, setEstimatedCompletionTime] = useState(null);
  const [quizStatData, setQuizStatData] = useState(false);
  const [quizStatApiResponse, setQuizStatApiResponse] = useState(null);
  const [isQuizFeedBackStatNull, setIsQuizFeedBackStatNull] = useState(false);
  const [isQuizStatDetailsNull, setIsQuizStatDetailsNull] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Context
  const { lessonInfo, lessonIdentifiers, updateLessonCompletionData } = useContext(LessonContext);
  const { notifyUser } = useContext(NofityUserContext);

  // API hooks
  const { data: saveQuixResponseData, postData: saveOptionQuizResponse } = usePostRequest(API_ENDPOINTS.SAVE_OPTION_QUIZ_PROGRESS);
  const { data: quizStatResponse, postData: quizStatPayload } = usePostRequest(API_ENDPOINTS.FETCH_QUIZ_STAT);
  const { postData: autoSaveQuizPayload } = usePostRequest(API_ENDPOINTS.POST_AUTO_SAVE_QUIZ);
  const { data: getAutoSaveResponse, getData: getAutoSaveQuizPayload } = useGetRequest(API_ENDPOINTS.GET_AUTO_SAVED_QUIZ_DATA);

  // Auto-save fetch on mount
  useEffect(() => {
    let isFetched = false;
    
    const fetchAutoSaveQuizPayload = async () => {
      if (isFetched) return;
      
      try {
        await getAutoSaveQuizPayload(`${content}/${lessonIdentifiers?.course_id}`);
        isFetched = true;
        console.log(isFetched)
      } catch (error) {
        console.error("Error fetching auto-save quiz payload:", error);
      }
    };

    if (content && lessonIdentifiers?.course_id) {
      fetchAutoSaveQuizPayload();
    }
  }, [content, lessonIdentifiers?.course_id]);

  // Process auto-saved data
  useEffect(() => {
    if (getAutoSaveResponse?.response && contentDetails?.multipleOptions) {
      try {
        const savedData = JSON.parse(getAutoSaveResponse.response);
        const savedOptions = {};
        
        savedData.forEach(item => {
          savedOptions[item.quiz_id] = item.option_id;
        });
        
        setActiveOption(savedOptions);
      } catch (error) {
        console.error("Error parsing auto-save data:", error);
      }
    }
  }, [getAutoSaveResponse, contentDetails]);

  // Existing helper functions
  const areAllQuestionsAnswered = () => {
    if (!contentDetails || !contentDetails.multipleOptions) return false;
    return contentDetails.multipleOptions.every(
      (question) => activeOption[question.question_id] !== undefined
    );
  };

  // Modified navigation handler with auto-save
  const handleNavigation = async (direction) => {
    if (quizData) {
      const { question_id } = quizData;

      if (!activeOption[question_id] && direction === "next") {
        notifyUser(
          "Select Option",
          "Please select an option before proceeding.",
          "warning"
        );
        return;
      }

      // Auto-save current progress
      try {
        const answeredQuestions = contentDetails.multipleOptions
          .slice(0, currentQuestionIndex + 1)
          .map(question => ({
            quiz_id: question.question_id,
            option_id: activeOption[question.question_id],
          }));

        await autoSaveQuizPayload({
          course_id: lessonIdentifiers.course_id,
          content_id: content,
          response: answeredQuestions,
        });
        console.log("Auto-saving answers:", answeredQuestions);
      } catch (error) {
        console.error("Error auto-saving quiz progress:", error);
      }

      const totalQuestions = contentDetails.multipleOptions.length;
      let newQuestionIndex = direction === "next" 
        ? currentQuestionIndex + 1 
        : currentQuestionIndex - 1;

      newQuestionIndex = Math.max(0, Math.min(newQuestionIndex, totalQuestions - 1));

      setCurrentQuestionIndex(newQuestionIndex);
      setQuizData(contentDetails.multipleOptions[newQuestionIndex]);
      setErrorMessage("");
    }
  };

  
  useEffect(() => {
    setTimerKey(timerKey + 1);
  }, [quizStarted]);

  useEffect(() => {
    if (lessonInfo) {
      const contentArray = lessonInfo || [];
      const foundContent = contentArray.find(
        (item) => item.content_id === content
      );

      if (foundContent) {
        setContentDetails(foundContent.contentDetails);
        setContentDetails1(foundContent);
        setLoading(true);
      }
    }
  }, [lessonInfo, content]);

  useEffect(() => {
    if (contentDetails && contentDetails.multipleOptions) {
      setQuizData(contentDetails.multipleOptions[currentQuestionIndex]);
    }
  }, [contentDetails, currentQuestionIndex]);

  useEffect(() => {
    if (contentDetails && contentDetails.multipleOptions.length > 0) {
      const firstQuestion = contentDetails.multipleOptions[0];
      setEstimatedCompletionTime(firstQuestion.estimated_completion_time);
    }
  }, [contentDetails]);

  useEffect(() => {
    if (contentDetails1 && !quizStatData && lessonIdentifiers?.course_id) {
      getQuizStatData(
        contentDetails1.RefContentType.name,
        contentDetails1.contentDetails.multipleOptions[0].quiz_op_id,
        lessonIdentifiers?.course_id
      );
      setQuizStatData(true);
    }
  }, [contentDetails1, lessonIdentifiers, quizStatPayload]);

  useEffect(() => {
    if (quizStatResponse && contentDetails1) {
      setQuizStatApiResponse(quizStatResponse);
      if (
        !quizStatResponse.quizfeedBackStat ||
        quizStatResponse.quizfeedBackStat.length === 0
      ) {
        setIsQuizFeedBackStatNull(true);
        setIsQuizStatDetailsNull(false);
        // updateLessonCompletionData(content);
      } else {
        setIsQuizFeedBackStatNull(false);
        setIsQuizStatDetailsNull(true);
      }

      if (
        !quizStatResponse.quizStatDetails ||
        quizStatResponse.quizStatDetails.length === 0
      ) {
        setIsQuizStatDetailsNull(true);
        setIsQuizFeedBackStatNull(true);
        setLoading(true);
      }
    }
  }, [quizStatResponse, contentDetails1]);

  useEffect(() => {
    if (quizStatApiResponse) {
      const combinedData = quizStatApiResponse.quizStatDetails.map((detail) => {
        const feedback = quizStatApiResponse.quizfeedBackStat.find(
          (fb) => fb.feedback.response_op_id === detail.response_quiz_op_id
        );

        const question =
          detail.MasterQuestion && detail.MasterQuestion
            ? detail.MasterQuestion.question
            : null;
        const questionScore =
          detail.MasterQuestion && detail.MasterQuestion
            ? detail.MasterQuestion.score
            : null;

        return {
          id: detail.response_quiz_op_id,
          question: question,
          answer: detail.answer_text,
          submission_time: detail.submission_time,
          feedback_submission_time: feedback
            ? feedback.feedback.updated_on
            : null,
          question_score: questionScore,
          feedback: feedback ? feedback.feedback.feedback_text : null,
          feedback_score: feedback ? feedback.feedback.score : null,
          completion_time: detail.completion_time,
        };
      });
      setModalData(combinedData);
    }
  }, [quizStatApiResponse]);

  useEffect(() => {
    if (quizStatResponse) {
      const { quizStatDetails } = quizStatResponse;
  
      if (Array.isArray(quizStatDetails) && quizStatDetails.length > 0) {
        console.log('quizStatDetails is non-empty:', quizStatDetails);
        updateLessonCompletionData(content);
      }

    }
  }, [quizStatResponse]);

  const getQuizStatData = async (ref_content_type, quiz_id, course_id) => {
    const payLoad = {
      course_id: course_id,
      quiz__id: quiz_id,
      ref_content_type: ref_content_type,
    };

    try {
      await quizStatPayload(payLoad);
    } catch (error) {
      console.error("Error fetching quiz stats:", error);
      notifyUser(
        "Error",
        "Failed to fetch quiz statistics. Please try again.",
        "error"
      );
    }
  };

  const handleOptionClick = (questionId, optionId) => {
    setActiveOption((prev) => ({
      ...prev,
      [questionId]: optionId,
    }));
    setErrorMessage("");
  };

  const handleStartQuiz = () => {
    setQuizStarted(true);
    setStartTime(new Date());
  };

  const handleSave = async () => {
    if (!areAllQuestionsAnswered()) {
      const unansweredQuestions = contentDetails.multipleOptions
        .filter((question) => !activeOption[question.question_id])
        .map((_, index) => index + 1)
        .join(", ");

      notifyUser(
        "Incomplete Quiz",
        `Please answer all questions before saving. Missing answers for question(s): ${unansweredQuestions}`,
        "warning"
      );
      return;
    }

    setIsSaving(true);
    const endTime = new Date();
    const timeTaken = ((endTime - startTime) / 1000 / 60).toFixed(2);

    try {
      for (const question of contentDetails.multipleOptions) {
        const selectedOptionId = activeOption[question.question_id];
        const selectedOption = question.options.find(
          (option) => option.option_id === selectedOptionId
        );
        const selectedText = selectedOption ? selectedOption.option_name : "";

        const payLoad = {
          course_id: lessonIdentifiers.course_id,
          quiz_op_id: question.quiz_op_id,
          quiz_id: question.question_id,
          option_id: selectedOptionId,
          answer_text: selectedText,
          completion_time: timeTaken,
        };

        await saveOptionQuizResponse(payLoad);
      }
      updateLessonCompletionData(content);

      setQuizSubmitted(true);
    } catch (error) {
      console.error("Failed to submit quiz responses:", error);
      notifyUser(
        "Error",
        "Failed to save quiz responses. Please try again.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (quizSubmitted) {
      getQuizStatData(
        contentDetails1.RefContentType.name,
        contentDetails1.contentDetails.multipleOptions[0].quiz_op_id,
        lessonIdentifiers?.course_id
      );
      const timer = setTimeout(() => {
        setQuizSubmitted(false);
        setLoading(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [quizSubmitted]);

  const handleTimeUp = async () => {
    setActiveOption({});
  };

  if (quizSubmitted) {
    return <QuizFeedback success={true} />;
  }

  if (!isQuizStatDetailsNull && isQuizFeedBackStatNull) {
    return (
      <QuizResultCard
        data={modalData}
        title={quizStatApiResponse.quizStatDetails[0].MasterQuizOption.quiz_name}
        date={quizStatApiResponse.quizStatDetails[0].submission_time}
        status="Submitted"
      />
    );
  }

  if (isQuizStatDetailsNull && !isQuizFeedBackStatNull) {
    return (
      <QuizResultCard
        data={modalData}
        title={
          quizStatApiResponse.quizStatDetails[0].MasterQuizOption.quiz_name
        }
        date={quizStatApiResponse.quizStatDetails[0].submission_time}
        score={quizStatApiResponse.quizfeedBackStat[0].feedback.score}
        status="Graded"
      />
    );
  }

  return display ? (
    <>
      {!quizStarted && (
        <QuizTimerStartOverlay handleStartQuiz={handleStartQuiz} />
      )}
      {quizStarted && (
        <Box className="SAQ_wrapper" sx={{ position: "relative" }}>
          <Box className="SAQ_wrapper__Question-box">
            <Box className="SAQ_wrapper__Question-box--number">
              <Typography className="SAQ_wrapper__Question-box--number-text">
                Question {currentQuestionIndex + 1} /{" "}
                {contentDetails.multipleOptions.length}
              </Typography>
            </Box>
            <Typography className="SAQ_wrapper__Question-box--question">
              {quizData.question}
            </Typography>
          </Box>
          <Box className="SAQ_wrapper__answer-wrapper">
            <Box
              className="SAQ_wrapper__options-wrapper"
              sx={{ width: "100%" }}
            >
              {estimatedCompletionTime && (
                <QuizTimer
                  key={timerKey}
                  estimatedCompletionTime={estimatedCompletionTime}
                  handleComplete={handleTimeUp}
                />
              )}
              <Grid container spacing={4} sx={{
                 padding: "20px",
                 justifyContent:
                 quizData.options.length === 3 ? "center" : "flex-start",
                 alignItems: "center",
                 textAlign: "center",

               }}
               data-options-length={quizData.options.length}
               >
                {quizData.options && quizData.options.length > 0 ? (
                  quizData.options.map((option) => (
                    <Grid item xs={12} sm={6} key={option.option_id}>
                      <Button
                        fullWidth
                        className={
                          activeOption[quizData.question_id] ===
                          option.option_id
                            ? "SAQ_wrapper__options-wrapper--option-button--active"
                            : "SAQ_wrapper__options-wrapper--option-button"
                        }
                        onClick={() =>
                          handleOptionClick(
                            quizData.question_id,
                            option.option_id
                          )
                        }
                        sx={{
                          minHeight: "120px",  
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          padding: "16px",
                          margin: "8px",
                          width: "100%",  
                          maxWidth: "100%", // Prevent overflow
                          whiteSpace: "normal",
                          lineHeight: "1.4",
                          wordBreak: "break-word", // Handle long words
                          "&:hover": {
                            backgroundColor: "#f5f5f5"
                          }
                        }}
                      >
                        {option.option_name}
                      </Button>
                    </Grid>
                  ))
                ) : (
                  <Typography>No options available for this question</Typography>
                )}
              </Grid>
            </Box>

            <Box
              className="SAQ_wrapper-navigate"
              id="no-bg"
              sx={{
                marginBottom: "1px",
              }}
            >
              <Button
                className="SAQ_wrapper-navigate__button"
                variant="outlined"
                id="white-bg"
                onClick={() => handleNavigation("previous")}
                disabled={currentQuestionIndex === 0}
              >
                Previous
              </Button>
              {currentQuestionIndex ===
              contentDetails.multipleOptions.length - 1 ? (
                <Button
                  className="SAQ_wrapper-navigate__button"
                  variant="outlined"
                  id="white-bg"
                  onClick={handleSave}
                  disabled={!areAllQuestionsAnswered() || isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              ) : (
                <Button
                  className="SAQ_wrapper-navigate__button"
                  variant="outlined"
                  id="white-bg"
                  onClick={() => handleNavigation("next")}
                  disabled={!activeOption[quizData.question_id]}
                >
                  Next
              </Button>
            )}
          </Box>
          {/* <LinearProgress
  className="SAQ_wrapper__progress"
  variant="determinate"
  value={((currentQuestionIndex + 1) / contentDetails.multipleOptions.length) * 100}
  sx={{
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '8px !important',
    borderBottomLeftRadius: '30px',
    borderBottomRightRadius: '30px',
    backgroundColor: '#2b3d4f !important',
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#39cccc !important',
    },
    zIndex: 20
  }}
/> */}
        </Box>
      </Box>
    )}
  </>
) : (
  <VideoPlayerSkeleton />

);
};

export default MultipleOptionsQuiz;